import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserService } from '../services/apiService';
import { Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

const ResetPasswordView: React.FC = () => {
    const [searchParams] = useSearchParams();
    // Il token arriva come query param (con un path param servirebbe useParams)
    const code = searchParams.get('code');

    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [status, setStatus] = useState<'verifying' | 'valid' | 'invalid' | 'submitting' | 'success'>('verifying');
    const [error, setError] = useState('');

    // 1. Verifica immediata se il token è valido
    useEffect(() => {
        if (!code) {
            setStatus('invalid');
            setError('Link non valido o incompleto.');
            return;
        }

        const verifyToken = async () => {
            try {
                // Il server risponde 200 anche per un link scaduto o già usato (data=false).
                const res = await UserService.checkRecoverToken(code);
                if (res?.data !== true) {
                    setStatus('invalid');
                    setError('Questo link è scaduto o è già stato usato.');
                    return;
                }
                setStatus('valid');
            } catch (err) {
                setStatus('invalid');
                setError('Questo link è scaduto o non è valido.');
            }
        };

        verifyToken();
    }, [code]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setError('Le password non coincidono.');
            return;
        }

        if (newPassword.length < 8) {
            setError('La password deve essere di almeno 8 caratteri.');
            return;
        }

        setStatus('submitting');
        setError('');

        try {
            if (!code) throw new Error("Token mancante");

            // Chiamata all'endpoint saveNewPassword
            await UserService.saveNewPassword({
                code: code,
                newPassword: newPassword,
                confirmPassword: confirmPassword
            });

            setStatus('success');
            // Nessun redirect al login: la pagina si apre dal link email nel browser e
            // l'utente può accedere dall'app o dal web.

        } catch (err: any) {
            setStatus('valid'); // Rimaniamo nel form
            setError(err.response?.data?.message || 'Errore durante il salvataggio della password.');
        }
    };

    if (status === 'verifying') {
        return <div className="min-h-screen flex items-center justify-center">Verifica link in corso...</div>;
    }

    if (status === 'invalid') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md w-full">
                    <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Link scaduto</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button onClick={() => navigate('/recover-password')} className="text-blue-600 font-medium hover:underline">
                        Richiedi un nuovo link
                    </button>
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md w-full">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Aggiornata!</h2>
                    <p className="text-gray-600">La tua password è stata modificata con successo.</p>
                    <p className="text-gray-600 mt-2">Ora puoi accedere con la nuova password, dall'<strong>app</strong> o dal <strong>gestionale web</strong>. Puoi chiudere questa pagina.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Reimposta Password</h2>

                {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 mb-6 text-sm rounded">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">Nuova Password</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type={showPassword ? "text" : "password"}
                                className="w-full pl-10 pr-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                placeholder="••••••••"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">Conferma Password</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="password"
                                className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={status === 'submitting'}
                        className={`w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-white font-semibold bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ${
                            status === 'submitting' ? 'opacity-70 cursor-not-allowed' : ''
                        }`}
                    >
                        {status === 'submitting' ? 'Salvataggio...' : 'Aggiorna Password'}
                    </button>
                </form>
            </div>
            <LegalFooter variant="public" className="fixed bottom-0 left-0 right-0" />
        </div>
    );
};

export default ResetPasswordView;